import { breakpoint, Breakpoints, getWindowWidth } from '@zocdoc/components/src/grid';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { white } from '@zocdoc/branding/colors';
import { PaddedContainer } from '@zocdoc/components/src/Layout';
import Facet from './Facet';
import MoreFilters from './MoreFilters';
import SortingDropdown from './SortingDropdown';
import { noop, debounce } from 'lodash';

export default class FacetedSearch extends Component {
    static propTypes = {
        facets: PropTypes.arrayOf(PropTypes.object),
        theme: PropTypes.object.isRequired,
        updateParameters: PropTypes.func,
        updateRenderOverlay: PropTypes.func,
    }

    static defaultProps = {
        facets: [],
        updateParameters: noop,
        updateRenderOverlay: noop,
    };

    state = {
        numberOfDisplayedFacets: null,
        openFacets: new Set(),
    };

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        const { numberOfDisplayedFacets } = this.state;
        const displayedFacets = numberOfDisplayedFacets
            ? this.props.facets.slice(0, numberOfDisplayedFacets)
            : this.props.facets;
        const hiddenFacets = numberOfDisplayedFacets
            ? this.props.facets.slice(numberOfDisplayedFacets)
            : [];

        return (
            <FacetedSearchBar>
                <Wrapper>
                    {displayedFacets.map((facet, index) => (
                        <Facet
                            key={`facet-${index}`}
                            {...facet}
                            data-test="facet-chip"
                            onHideDropdown={this.handleHideFacetDropdown}
                            onShowDropdown={this.handleShowFacetDropdown}
                            updateParameters={this.props.updateParameters}
                        />
                    ))}
                    {this.maybeRenderMoreFilters(hiddenFacets)}
                </Wrapper>
                <StyledSortingDropdown
                    onHideDropdown={this.handleHideFacetDropdown}
                    onShowDropdown={this.handleShowFacetDropdown}
                    theme={this.props.theme}
                    updateParameters={this.props.updateParameters}
                />
            </FacetedSearchBar>
        );
    }

    maybeRenderMoreFilters(hiddenFacets) {
        if (hiddenFacets.length) {
            return (
                <MoreFilters
                    data-test="facet-more-filters"
                    moreFilters={hiddenFacets}
                    onHideDropdown={this.handleHideFacetDropdown}
                    onShowDropdown={this.handleShowFacetDropdown}
                    updateParameters={this.props.updateParameters}
                />
            );
        }
    }

    /*
    We need to maintain a set of open facets because clickOutside is implemented for each facet.
    We need to keep track of whether at least one facet is "open" at all times because that is what
    governs whether the searchoverlay is rendered or not on the search page.
    */

    handleHideFacetDropdown = instance => {
        const { openFacets } = this.state;
        const nextOpenFacets = new Set(openFacets);
        nextOpenFacets.delete(instance);

        this.setState({
            openFacets: nextOpenFacets,
        });

        if (nextOpenFacets.size === 0) {
            this.props.updateRenderOverlay(false);
        } else {
            this.props.updateRenderOverlay(true);
        }
    }

    handleResize = debounce(() => {
        const windowWidth = getWindowWidth();
        let numberOfDisplayedFacets;

        if (windowWidth < Breakpoints.small || windowWidth > Breakpoints.xlarge) {
            numberOfDisplayedFacets = null;
        } else if (windowWidth < Breakpoints.medium) {
            numberOfDisplayedFacets = 3;
        } else if (windowWidth < Breakpoints.large) {
            numberOfDisplayedFacets = 4;
        } else if (windowWidth < Breakpoints.xlarge) {
            numberOfDisplayedFacets = 5;
        }

        if (numberOfDisplayedFacets !== this.state.numberOfDisplayedFacets) {
            this.setState({ numberOfDisplayedFacets });
        }
    }, 500);

    handleShowFacetDropdown = instance => {
        this.setState(prevState => {
            const nextOpenFacets = new Set(prevState.openFacets);
            nextOpenFacets.add(instance);
            return { openFacets: nextOpenFacets };
        });

        this.props.updateRenderOverlay(true);
    }
}

const FacetedSearchBar = styled.div`
    background: ${white};
    display: flex;
    justify-content: space-between;
    margin-bottom: 1px;
    margin-top: 1px;
    padding-bottom: 14px;
    padding-top: 9.5px;

    ${breakpoint('xsmall', { down: true })} {
        overflow: hidden;
        height: 40px;
    }
`;

const Wrapper = styled(PaddedContainer)`
    background: ${white};
    display: flex;

    ${breakpoint('xsmall', { down: true })} {
        overflow: auto;
        white-space: nowrap;
        -webkit-overflow-scrolling: touch;
        height: 100px;
    }
`;

const StyledSortingDropdown = styled(SortingDropdown)`
    display: none;
    margin-right: 80px;

    ${breakpoint.small} {
        display: block !important;
    }
`;
